import React, { useState, useCallback, ChangeEvent } from 'react';
import { Box, Typography, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';

import {
  SpMatchesDefaultModal,
  SpMatchesDefaultTableList,
  SpMatchesDefaultTableRow,
  SpMatchesError,
} from 'components/SpMatchesDefaultTable';
import { SnackbarContainer, MOBILE_TOOLBAR_TOTAL } from 'components/standalone';
import { ModelContainer } from '../containers/useModelContainer';

interface SpMatchesSectionProps {
  canEdit: boolean;
}

export const SpMatchesSection: React.FC<SpMatchesSectionProps> = ({
  canEdit,
}) => {
  const {
    selectedModel,
    spModels,
    spModelsLoading: loading,
    createSpModel,
    updateSpModelActive,
    loadSpModels,
  } = ModelContainer.useContainer();
  const classes = useStyles();
  const { notify } = SnackbarContainer.useContainer();
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<SpMatchesError | null>(null);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
    setError(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setError(null);
  }, []);

  const handleAddNewItem = useCallback(
    async (spName: string, active: boolean) => {
      if (!selectedModel) return;

      const trimmedName = spName.trim();
      if (trimmedName.length === 0) {
        setError({ message: 'Model name cannot be empty.' });
        return;
      }

      try {
        const result = await createSpModel(
          selectedModel.id,
          selectedModel.subdivisionId,
          trimmedName,
          active,
        );
        setError(null);
        notify('SupplyPro model name created!', 'success');
        return result;
      } catch (err) {
        setError({ message: 'Failed to add SupplyPro model name.' });
        notify('Failed to add SupplyPro model name', 'error');
        if (selectedModel?.id) {
          loadSpModels(selectedModel.id);
        }
      }
    },
    [selectedModel, createSpModel, notify, loadSpModels],
  );

  const handleUpdateActiveFlag = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, id: string) => {
      try {
        await updateSpModelActive(id, event.target.checked);
        notify('SupplyPro model name updated!', 'success');
      } catch (err) {
        console.error('Error updating SP model:', err);
        notify('Failed to update SupplyPro model name', 'error');
      }
    },
    [updateSpModelActive, notify],
  );

  const rows: SpMatchesDefaultTableRow[] | null =
    spModels?.map(model => ({
      id: model.id,
      name: model.spName,
      active: model.active,
    })) || null;

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.heading} variant="h6" component="h3">
        SupplyPro - Potential Matches
      </Typography>

      <SpMatchesDefaultTableList
        columns={['SupplyPro Potential Matches']}
        rows={rows}
      />

      {canEdit && (
        <>
          <SpMatchesDefaultModal
            open={showModal}
            onClose={handleCloseModal}
            error={error}
            columns={['SupplyPro Potential Matches']}
            onUpdateActiveFlag={handleUpdateActiveFlag}
            onAddNewItem={handleAddNewItem}
            rows={rows}
          />

          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleOpenModal}
            className={classes.editButton}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: '500px',
    marginTop: '24px',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: MOBILE_TOOLBAR_TOTAL + 20,
    },
  },
  heading: {
    marginBottom: '16px',
  },
  editButton: {
    marginTop: '24px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px',
  },
}));

export default SpMatchesSection;
